import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION, User } from 'src/features/auth/types';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { userMessages } from 'src/features/users/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import CellUserActions from 'src/features/users/components/tables/CellUserActions';
import CreateUserFormModal from 'src/features/users/components/forms/CreateUserFormModal';
import { useListUsers } from 'src/features/users/api/useListUsers';
import { booleanBadgeOptions } from 'src/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};

const columnHelper = createColumnHelper<User>();

type UsersProps = {
    hideUsersActions?: boolean;
};

function Users({ hideUsersActions = false }: UsersProps) {
    const { formatMessage } = useIntl();
    const permissionManageUsers = useHasPermissions([PERMISSION.MANAGE_USERS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch();
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { data, isLoading } = useListUsers({
        ...queryFiltersOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.name),
            },
        }),
        columnHelper.accessor('username', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.username),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(userMessages.active),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageUsers && !hideUsersActions
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellUserActions,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<User, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(userMessages.users),
                entryName: formatMessage(userMessages.user).toLowerCase(),
                entryNames: formatMessage(userMessages.users).toLowerCase(),
                placeholder: formatMessage(userMessages.user),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageUsers && !hideUsersActions && { newEntry: CreateUserFormModal }),
            }}
        />
    );
}

export default Users;
